import type { EditorAccessMode, EditorTabKind } from './editorWorkspaceIds';
import { appendChromeMinimal, appendEmbedded } from './editorChrome';
import { documentEditorHref } from '../documentEditor/documentEditorUrls';
import type { DocumentEditorKind } from '../documentEditor/documentFileTypes';
import { drawioEditorHref } from '../drawioEditor/drawioEditorUrls';
import { markdownEditorHref } from '../markdownEditor/markdownEditorUrls';
import { mediaViewerHref } from '../mediaViewer/mediaPreviewUrls';
import type { MediaKind } from '../mediaViewer/mediaFileTypes';
import { pdfViewerHref } from '../pdfViewer/pdfPreviewUrls';
import { codeEditorHref } from '../codeEditor/codeEditorUrls';
import { textEditorHref } from '../textEditor/textEditorUrls';

export const EDITOR_WORKSPACE_ROUTE = '/files/workspace';

export function editorRouteForKind(kind: EditorTabKind): string {
  switch (kind) {
    case 'text':
      return '/files/text-editor';
    case 'code':
      return '/files/code-editor';
    case 'markdown':
      return '/files/markdown-editor';
    case 'drawio':
      return '/files/drawio-editor';
    case 'document':
      return '/files/document-editor';
    case 'spreadsheet':
      return '/files/spreadsheet-editor';
    case 'pdf':
      return '/files/pdf-viewer';
    case 'media':
      return '/files/image-viewer';
    default:
      return '/files/text-editor';
  }
}

export function editorHrefForKind(
  kind: EditorTabKind,
  mountId: string,
  path: string,
  mediaKind?: MediaKind,
  accessMode: EditorAccessMode = 'edit'
): string {
  const appendViewMode = (href: string) =>
    accessMode === 'view' ? `${href}&mode=view` : href;
  switch (kind) {
    case 'text':
      return appendViewMode(textEditorHref(mountId, path));
    case 'code':
      return appendViewMode(codeEditorHref(mountId, path));
    case 'markdown':
      return appendViewMode(markdownEditorHref(mountId, path));
    case 'drawio':
      return appendViewMode(drawioEditorHref(mountId, path));
    case 'document':
      return documentEditorHref(
        'richedit',
        accessMode === 'view' ? 'preview' : 'edit',
        mountId,
        path
      );
    case 'spreadsheet':
      return documentEditorHref(
        'spreadsheet',
        accessMode === 'view' ? 'preview' : 'edit',
        mountId,
        path
      );
    case 'pdf':
      return appendViewMode(pdfViewerHref(mountId, path));
    case 'media':
      return mediaViewerHref(mediaKind ?? 'image', mountId, path);
    default:
      return textEditorHref(mountId, path);
  }
}

export function documentKindToTabKind(kind: DocumentEditorKind): 'document' | 'spreadsheet' {
  return kind === 'spreadsheet' ? 'spreadsheet' : 'document';
}

export function buildWorkspaceTabSrc(
  kind: EditorTabKind,
  mountId: string,
  path: string,
  mediaKind?: MediaKind,
  accessMode: EditorAccessMode = 'edit'
): string {
  // Both flags: layout skips AppShell for either; editors treat embedded as no chrome.
  return appendChromeMinimal(
    appendEmbedded(editorHrefForKind(kind, mountId, path, mediaKind, accessMode))
  );
}

export function buildMinimalBrowserTabHref(href: string): string {
  return appendChromeMinimal(href);
}

export function workspaceHref(tabId: string): string {
  const params = new URLSearchParams();
  params.set('tab', tabId);
  return `${EDITOR_WORKSPACE_ROUTE}?${params.toString()}`;
}
